import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Home } from 'lucide-react';
import { useAuthStore } from './store/authStore';
import { useI18n } from './i18n';

// Lazy-load all pages so the initial JS bundle is minimal.
// Each page chunk is only downloaded + parsed when first navigated to,
// which significantly reduces startup time on Android TV boxes.
const LoginPage            = lazy(() => import('./pages/LoginPage'));
const TvDisplayPage        = lazy(() => import('./pages/TvDisplayPage'));
const GamePage             = lazy(() => import('./pages/GamePage'));
const WaitingListPage      = lazy(() => import('./pages/WaitingListPage'));
const StatsPage            = lazy(() => import('./pages/StatsPage'));
const SettingsPage         = lazy(() => import('./pages/SettingsPage'));
const StaffManagementPage  = lazy(() => import('./pages/StaffManagementPage'));
const TablesPage           = lazy(() => import('./pages/TablesPage'));
const EightBallPage        = lazy(() => import('./pages/EightBallPage'));
const TournamentsPage      = lazy(() => import('./pages/TournamentsPage'));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage'));

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-felt-900 flex flex-col items-center justify-center gap-6 p-8 text-center">
      <p className="font-display text-gold-400 text-6xl font-black tracking-widest">404</p>
      <p className="text-gray-400 text-sm tracking-widest uppercase">Page not found</p>
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-felt-700 hover:bg-felt-600
                   text-white font-bold border border-felt-600 transition-colors
                   focus-visible:ring-2 focus-visible:ring-gold-400 outline-none"
      >
        <Home size={16} strokeWidth={1.5} />
        Go home
      </button>
    </div>
  );
}

function LoadingFallback() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-felt-900 flex items-center justify-center">
      <p className="text-gold-400 text-xl">{t.common.loading}</p>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuthStore();
  if (!currentUser) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireRole({
  children,
  roles,
}: {
  children: React.ReactNode;
  roles: string[];
}) {
  const { currentUser } = useAuthStore();
  if (!currentUser || !roles.includes(currentUser.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function AppRoutes() {
  const location = useLocation();
  return (
    <div key={location.key} className="animate-page-enter">
    <Suspense fallback={<LoadingFallback />}>
      <Routes location={location}>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <TvDisplayPage />
            </RequireAuth>
          }
        />
        <Route
          path="/snooker"
          element={
            <RequireAuth>
              <GamePage />
            </RequireAuth>
          }
        />
        <Route
          path="/snooker/:tableId"
          element={
            <RequireAuth>
              <GamePage />
            </RequireAuth>
          }
        />
        <Route
          path="/waiting"
          element={
            <RequireAuth>
              <WaitingListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/stats"
          element={
            <RequireRole roles={['superadmin', 'manager', 'staff']}>
              <StatsPage />
            </RequireRole>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireRole roles={['superadmin']}>
              <SettingsPage />
            </RequireRole>
          }
        />
        <Route
          path="/settings/staff"
          element={
            <RequireRole roles={['superadmin', 'manager']}>
              <StaffManagementPage />
            </RequireRole>
          }
        />
        <Route
          path="/tables"
          element={
            <RequireRole roles={['superadmin', 'manager']}>
              <TablesPage />
            </RequireRole>
          }
        />
        <Route
          path="/8ball"
          element={
            <RequireAuth>
              <EightBallPage />
            </RequireAuth>
          }
        />
        <Route
          path="/8ball/:tableId"
          element={
            <RequireAuth>
              <EightBallPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tournaments"
          element={
            <RequireAuth>
              <TournamentsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tournaments/:id"
          element={
            <RequireAuth>
              <TournamentDetailPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </div>
  );
}
